import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Trash2, Check, X, MapPin, Plus, Star, Mail, User, FileText, ExternalLink, Download } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { OPPORTUNITY_TYPES, INDUSTRIES, SKILL_TAGS } from '../constants';

const emptyListing = {
  title: '',
  opportunity_type: '',
  industry: '',
  organisation: '',
  city: '',
  country: 'India',
  salary: '',
  description: '',
  apply_link: '',
  allow_platform_application: false,
  closing_date: '',
  tags: []
};

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [talentProfiles, setTalentProfiles] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [crewProfiles, setCrewProfiles] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [showCrewDialog, setShowCrewDialog] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState('all');
  const [inquiryTypeFilter, setInquiryTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingListing, setEditingListing] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAppDetailDialog, setShowAppDetailDialog] = useState(false);
  const [showTalentDetailDialog, setShowTalentDetailDialog] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [talentNotes, setTalentNotes] = useState('');
  const [newListing, setNewListing] = useState({ ...emptyListing });
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [appStatusFilter, setAppStatusFilter] = useState('all');
  const [talentRoleFilter, setTalentRoleFilter] = useState('all');
  const [talentStageFilter, setTalentStageFilter] = useState('all');
  const [talentOptInOnly, setTalentOptInOnly] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAllListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const { data } = await axios.get(`${API_URL}/api/listings${params}`, {
        withCredentials: true
      });
      setListings(data);
    } catch (error) {
      toast.error('Failed to fetch listings');
    } finally {
      setLoading(false);
    }
  }, [API_URL, statusFilter]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = appStatusFilter !== 'all' ? `?status=${appStatusFilter}` : '';
      const { data } = await axios.get(`${API_URL}/api/applications${params}`, {
        withCredentials: true
      });
      setApplications(data);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  }, [API_URL, appStatusFilter]);

  const fetchTalent = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (talentRoleFilter !== 'all') params.append('role', talentRoleFilter);
      if (talentStageFilter !== 'all') params.append('stage', talentStageFilter);
      if (talentOptInOnly) params.append('email_opt_in', 'true');
      const { data } = await axios.get(`${API_URL}/api/talent?${params.toString()}`, {
        withCredentials: true
      });
      setTalentProfiles(data);
    } catch (error) {
      toast.error('Failed to fetch talent profiles');
    } finally {
      setLoading(false);
    }
  }, [API_URL, talentRoleFilter, talentStageFilter, talentOptInOnly]);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (inquiryStatusFilter !== 'all') params.append('status', inquiryStatusFilter);
      if (inquiryTypeFilter !== 'all') params.append('inquiry_type', inquiryTypeFilter);
      const { data } = await axios.get(`${API_URL}/api/admin/inquiries?${params.toString()}`, {
        withCredentials: true
      });
      setInquiries(data);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
    } finally {
      setLoading(false);
    }
  }, [API_URL, inquiryStatusFilter, inquiryTypeFilter]);

  const fetchCrew = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/crew`, { withCredentials: true });
      setCrewProfiles(data);
    } catch (error) {
      toast.error('Failed to fetch crew profiles');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const openCrewDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/crew/${id}`, { withCredentials: true });
      setSelectedCrew(data);
      setShowCrewDialog(true);
    } catch (error) {
      toast.error('Failed to load crew profile');
    }
  };

  const exportCrewCsv = () => {
    if (!crewProfiles.length) { toast.error('No crew profiles to export'); return; }
    const headers = ['Submitted','Name','Phone','Email','City','Travels','Travel Cities','Gig Roles','Other Role','Events Worked','Cultural Exp','Cultural Example','Short Notice','Notice Period','Days','Employment','Strength','Instagram','Status'];
    const esc = (v) => v === null || v === undefined ? '' : `"${String(v).replace(/"/g,'""')}"`;
    const rows = crewProfiles.map((c) => [
      c.submitted_at, c.full_name, c.phone, c.email, c.city, c.travels, c.travel_cities,
      (c.gig_roles||[]).join('; '), c.gig_roles_other, c.events_worked, c.cultural_experience,
      c.cultural_example, c.short_notice, c.notice_period, (c.days_available||[]).join('; '),
      c.employment_status, c.about_strength, c.instagram_handle, c.status
    ].map(esc).join(','));
    const csv = [headers.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `crew_${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/inquiries/${id}`, { status }, { withCredentials: true });
      toast.success('Inquiry updated');
      fetchInquiries();
      if (selectedInquiry?.id === id) {
        setSelectedInquiry({ ...selectedInquiry, status });
      }
    } catch (error) {
      toast.error('Failed to update inquiry');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/inquiries/${id}`, { withCredentials: true });
      toast.success('Inquiry deleted');
      setShowInquiryDialog(false);
      setSelectedInquiry(null);
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const exportInquiriesCsv = () => {
    if (!inquiries.length) {
      toast.error('No inquiries to export');
      return;
    }
    const headers = ['Submitted','Name','Email','Phone','Organisation','Org Type','Role','Timeline','Inquiry Type','Status','Message'];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const rows = inquiries.map((i) => [
      i.submitted_at, i.name, i.email, i.phone, i.organisation, i.organisation_type,
      i.role, i.timeline, i.inquiry_type, i.status, i.message
    ].map(escape).join(','));
    const csv = [headers.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inquiries_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!authLoading) {
      if (!user || user.role !== 'admin') {
        navigate('/admin/login');
      } else {
        fetchAllListings();
        fetchApplications();
        fetchTalent();
        fetchInquiries();
        fetchCrew();
      }
    }
  }, [user, authLoading, navigate, fetchAllListings, fetchApplications, fetchTalent, fetchInquiries, fetchCrew]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      if (activeTab === 'listings') fetchAllListings();
      else if (activeTab === 'applications') fetchApplications();
      else if (activeTab === 'talent') fetchTalent();
      else if (activeTab === 'inquiries') fetchInquiries();
      else if (activeTab === 'crew') fetchCrew();
    }
  }, [statusFilter, appStatusFilter, talentRoleFilter, talentStageFilter, talentOptInOnly, inquiryStatusFilter, inquiryTypeFilter, user, fetchAllListings, fetchApplications, fetchTalent, fetchInquiries, fetchCrew, activeTab]);

  const handleApprove = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/listings/${id}/approve`, {}, { withCredentials: true });
      toast.success('Listing approved');
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to approve listing');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/listings/${id}/reject`, {}, { withCredentials: true });
      toast.success('Listing rejected');
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to reject listing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await axios.delete(`${API_URL}/api/listings/${id}`, { withCredentials: true });
      toast.success('Listing deleted');
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const { data } = await axios.patch(`${API_URL}/api/listings/${id}/feature`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to toggle featured');
    }
  };

  const handleTogglePremium = async (id) => {
    try {
      const { data } = await axios.patch(`${API_URL}/api/listings/${id}/premium`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to toggle premium');
    }
  };

  const handleEdit = (listing) => {
    setEditingListing({ ...listing });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/api/listings/${editingListing.id}`,
        editingListing,
        { withCredentials: true }
      );
      toast.success('Listing updated');
      setShowEditDialog(false);
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to update listing');
    }
  };

  const handleAddListing = async (e) => {
    e.preventDefault();
    if (!newListing.opportunity_type || !newListing.industry) {
      toast.error('Please select an opportunity type and industry.');
      return;
    }
    setAddSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/listings`, newListing, { withCredentials: true });
      toast.success('Listing created and published!');
      setShowAddDialog(false);
      setNewListing({ ...emptyListing });
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to create listing');
    } finally {
      setAddSubmitting(false);
    }
  };

  const toggleNewTag = (tag) => {
    setNewListing((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag]
    }));
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleAppStatusChange = async (appId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/applications/${appId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Application marked as ${newStatus}`);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteApp = async (appId) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await axios.delete(`${API_URL}/api/applications/${appId}`, { withCredentials: true });
      toast.success('Application deleted');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleClearAllApplications = async () => {
    if (!window.confirm('Delete ALL applications? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/applications`, { withCredentials: true });
      toast.success('All applications deleted');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleClearAllListings = async () => {
    if (!window.confirm('Delete ALL listings? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/listings`, { withCredentials: true });
      toast.success('All listings deleted');
      fetchAllListings();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleViewTalent = (profile) => {
    setSelectedTalent(profile);
    setTalentNotes(profile.admin_notes || '');
    setShowTalentDetailDialog(true);
  };

  const handleSaveTalentNotes = async () => {
    try {
      await axios.patch(`${API_URL}/api/talent/${selectedTalent.id}/notes`, { notes: talentNotes, status: 'reviewed' }, { withCredentials: true });
      toast.success('Notes saved');
      fetchTalent();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleDeleteTalent = async (id) => {
    if (!window.confirm('Delete this profile?')) return;
    try {
      await axios.delete(`${API_URL}/api/talent/${id}`, { withCredentials: true });
      toast.success('Profile deleted');
      fetchTalent();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const appStatusColor = (status) => {
    const map = {
      submitted: 'bg-blue-100 text-blue-800',
      reviewed: 'bg-yellow-100 text-yellow-800',
      shortlisted: 'bg-green-100 text-green-800',
      contacted: 'bg-purple-100 text-purple-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return map[status] || 'bg-zinc-100 text-zinc-800';
  };

  if (authLoading || !user) {
    return <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />

      <div className="px-6 md:px-12 lg:px-24 py-20">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-12">
          <div>
            <h1
              className="text-4xl md:text-5xl font-light tracking-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
            >
              Admin Dashboard
            </h1>
            <p className="text-base" style={{ color: '#0F2942' }}>Manage all listings and applications</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${API_URL}/api/admin/export/resumes`}
              className="inline-flex items-center rounded-lg px-6 py-3 text-sm font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: '#D4820A', color: '#0F2942' }}
              data-testid="admin-export-resumes-zip"
            >
              <Download className="w-4 h-4 mr-2" /> Export All Resumes (ZIP)
            </a>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="rounded-lg px-6 py-3"
              style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}
              data-testid="admin-add-listing-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Listing
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-8" style={{ borderBottom: '1px solid #F0EBE2' }}>
          <button
            onClick={() => setActiveTab('listings')}
            className="px-6 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'listings' ? '#0F2942' : '#0F2942',
              borderBottom: activeTab === 'listings' ? '2px solid #0F2942' : '2px solid transparent',
              marginBottom: '-1px'
            }}
            data-testid="tab-listings"
          >
            Listings
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className="px-6 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'applications' ? '#0F2942' : '#0F2942',
              borderBottom: activeTab === 'applications' ? '2px solid #0F2942' : '2px solid transparent',
              marginBottom: '-1px'
            }}
            data-testid="tab-applications"
          >
            Applications {applications.length > 0 && <span className="ml-1.5 text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: 'rgba(212,130,10,0.2)', color: '#D4820A' }}>{applications.length}</span>}
          </button>
          <button
            onClick={() => setActiveTab('talent')}
            className="px-6 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'talent' ? '#0F2942' : '#0F2942',
              borderBottom: activeTab === 'talent' ? '2px solid #0F2942' : '2px solid transparent',
              marginBottom: '-1px'
            }}
            data-testid="tab-talent"
          >
            Talent Pool {talentProfiles.length > 0 && <span className="ml-1.5 text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: 'rgba(212,130,10,0.2)', color: '#D4820A' }}>{talentProfiles.length}</span>}
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className="px-6 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'inquiries' ? '#0F2942' : '#0F2942',
              borderBottom: activeTab === 'inquiries' ? '2px solid #0F2942' : '2px solid transparent',
              marginBottom: '-1px'
            }}
            data-testid="tab-inquiries"
          >
            Inquiries {inquiries.length > 0 && <span className="ml-1.5 text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>{inquiries.length}</span>}
          </button>
          <button
            onClick={() => setActiveTab('crew')}
            className="px-6 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'crew' ? '#0F2942' : '#0F2942',
              borderBottom: activeTab === 'crew' ? '2px solid #0F2942' : '2px solid transparent',
              marginBottom: '-1px'
            }}
            data-testid="tab-crew"
          >
            Crew {crewProfiles.length > 0 && <span className="ml-1.5 text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: 'rgba(212,130,10,0.2)', color: '#D4820A' }}>{crewProfiles.length}</span>}
          </button>
        </div>

        {/* Listings Tab */}
        {activeTab === 'listings' && (
        <>
        <div className="flex items-center gap-4 mb-8">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-lg w-64 bg-white" data-testid="admin-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Listings</SelectItem>
              <SelectItem value="pending">Pending Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={handleClearAllListings} className="rounded-lg text-xs hover:bg-red-50 hover:text-red-600" style={{ color: '#0F2942' }} data-testid="clear-all-listings">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Clear All Listings
          </Button>
        </div>

        {loading ? (
          <p className="text-zinc-500">Loading...</p>
        ) : listings.length === 0 ? (
          <p className="text-zinc-500">No listings found</p>
        ) : (
          <div className="space-y-4" data-testid="admin-listings-container">
            {listings.map((listing) => (
              <div
                key={listing.id}
                className="border rounded-xl p-6 bg-white hover:shadow-md transition-all"
                style={{ borderColor: '#F0EBE2' }}
                data-testid={`admin-listing-${listing.id}`}
              >
                <div className="flex justify-between items-start gap-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span
                        className={`text-xs uppercase tracking-[0.15em] px-2 py-1 ${
                          listing.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : listing.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {listing.status}
                      </span>
                      <span className="text-xs uppercase tracking-[0.15em] font-medium bg-zinc-900 text-white px-2 py-1">
                        {listing.opportunity_type}
                      </span>
                      <span className="text-xs uppercase tracking-[0.15em] text-zinc-500 border border-zinc-300 px-2 py-1">
                        {listing.industry}
                      </span>
                    </div>
                    <h3
                      className="text-xl font-normal tracking-tight mb-1"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {listing.title}
                    </h3>
                    <p className="text-sm text-zinc-600 mb-1">
                      {listing.organisation}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                      <MapPin className="w-3 h-3" strokeWidth={1.5} />
                      <span>{listing.city}, {listing.country}</span>
                      <span className="mx-2">&middot;</span>
                      <span>Closes: {formatDate(listing.closing_date)}</span>
                    </div>
                    {listing.tags && listing.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {listing.tags.map((tag) => (
                          <span key={tag} className="text-xs text-zinc-500 bg-zinc-100 px-2 py-0.5">{tag}</span>
                        ))}
                      </div>
                    )}
                    {(listing.poster_name || listing.poster_email || listing.poster_phone) && (
                      <div className="mt-3 pt-3 flex flex-wrap items-center gap-4" style={{ borderTop: '1px solid #F0EBE2' }}>
                        <p className="text-xs uppercase tracking-[0.15em] font-medium" style={{ color: '#D4820A' }}>Posted by</p>
                        {listing.poster_name && (
                          <span className="text-xs flex items-center gap-1" style={{ color: '#0F2942' }}>
                            <User className="w-3 h-3" strokeWidth={1.5} /> {listing.poster_name}
                          </span>
                        )}
                        {listing.poster_email && (
                          <a href={`mailto:${listing.poster_email}`} className="text-xs flex items-center gap-1 hover:underline" style={{ color: '#0F2942' }}>
                            <Mail className="w-3 h-3" strokeWidth={1.5} /> {listing.poster_email}
                          </a>
                        )}
                        {listing.poster_phone && (
                          <a href={`tel:${listing.poster_phone}`} className="text-xs flex items-center gap-1 hover:underline" style={{ color: '#0F2942' }}>
                            <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> {listing.poster_phone}
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant={listing.premium ? "default" : "outline"}
                      onClick={() => handleTogglePremium(listing.id)}
                      className={`rounded-lg text-xs ${listing.premium ? 'text-white' : ''}`}
                      style={listing.premium ? { backgroundColor: '#D4820A' } : {}}
                      title={listing.premium ? 'Remove from premium' : 'Mark as premium'}
                      data-testid={`premium-${listing.id}`}
                    >
                      {listing.premium ? 'Premium' : 'Premium'}
                    </Button>
                    <Button
                      size="sm"
                      variant={listing.featured ? "default" : "outline"}
                      onClick={() => handleToggleFeatured(listing.id)}
                      className={`rounded-none ${listing.featured ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                      title={listing.featured ? 'Remove from featured' : 'Feature this listing'}
                      data-testid={`feature-${listing.id}`}
                    >
                      <Star className={`w-4 h-4 ${listing.featured ? 'fill-white' : ''}`} />
                    </Button>
                    {listing.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(listing.id)}
                          className="rounded-none bg-green-600 hover:bg-green-700"
                          data-testid={`approve-${listing.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleReject(listing.id)}
                          className="rounded-none bg-red-600 hover:bg-red-700"
                          data-testid={`reject-${listing.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(listing)}
                      className="rounded-none"
                      data-testid={`edit-${listing.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(listing.id)}
                      className="rounded-none"
                      data-testid={`delete-${listing.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
        <>
          <div className="flex items-center gap-4 mb-8">
            <Select value={appStatusFilter} onValueChange={setAppStatusFilter}>
              <SelectTrigger className="rounded-lg w-64 bg-white" data-testid="app-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Applications</SelectItem>
                <SelectItem value="submitted">New / Submitted</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="shortlisted">Shortlisted</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={handleClearAllApplications} className="rounded-lg text-xs hover:bg-red-50 hover:text-red-600" style={{ color: '#0F2942' }} data-testid="clear-all-applications">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Clear All
            </Button>
          </div>

          {loading ? (
            <p style={{ color: '#0F2942' }}>Loading...</p>
          ) : applications.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: '#F0EBE2' }} strokeWidth={1} />
              <p style={{ color: '#0F2942' }}>No applications found</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-applications-container">
              {applications.map((app) => (
                <div
                  key={app.id}
                  className="bg-white rounded-xl p-6 hover:shadow-md transition-all"
                  style={{ border: '1px solid #F0EBE2' }}
                  data-testid={`application-${app.id}`}
                >
                  <div className="flex justify-between items-start gap-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${appStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                        <span className="text-xs" style={{ color: '#0F2942' }}>
                          {formatDateTime(app.submitted_at)}
                        </span>
                      </div>

                      <div className="mb-3">
                        <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Applied for</p>
                        <p className="text-base font-medium" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942', fontSize: '1.15rem' }}>
                          {app.listing_title}
                        </p>
                        <p className="text-xs" style={{ color: '#0F2942' }}>{app.listing_organisation}</p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 flex-shrink-0" style={{ color: '#D4820A' }} strokeWidth={1.5} />
                          <span className="text-sm font-medium" style={{ color: '#0F2942' }} data-testid={`app-name-${app.id}`}>{app.applicant_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 flex-shrink-0" style={{ color: '#D4820A' }} strokeWidth={1.5} />
                          <a href={`mailto:${app.applicant_email}`} className="text-sm hover:underline" style={{ color: '#0F2942' }} data-testid={`app-email-${app.id}`}>{app.applicant_email}</a>
                        </div>
                      </div>

                      <button
                        onClick={() => { setSelectedApp(app); setShowAppDetailDialog(true); }}
                        className="text-sm font-medium hover:underline"
                        style={{ color: '#0F2942' }}
                        data-testid={`view-app-${app.id}`}
                      >
                        View Cover Letter &rarr;
                      </button>
                    </div>

                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Select
                        value={app.status}
                        onValueChange={(val) => handleAppStatusChange(app.id, val)}
                      >
                        <SelectTrigger className="rounded-lg w-36 text-xs h-9 bg-white" data-testid={`app-status-select-${app.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="shortlisted">Shortlisted</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteApp(app.id)}
                        className="rounded-lg text-xs h-9 hover:bg-red-50 hover:text-red-600"
                        style={{ color: '#0F2942' }}
                        data-testid={`delete-app-${app.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
        )}

        {/* Talent Pool Tab */}
        {activeTab === 'talent' && (
        <>
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <Select value={talentRoleFilter} onValueChange={setTalentRoleFilter}>
              <SelectTrigger className="rounded-lg w-56 bg-white" data-testid="talent-role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {['Artist','Designer','Curation & Research','Museum/Gallery','Marketing & Communications','Partnerships','Events & Production','Education','Operations & Administration','Writing & Editorial','Other'].map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={talentStageFilter} onValueChange={setTalentStageFilter}>
              <SelectTrigger className="rounded-lg w-48 bg-white" data-testid="talent-stage-filter">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {['Student','Emerging','Early Career','Mid-Level','Senior/Leadership'].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <label
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white cursor-pointer"
              style={{ border: '1px solid #F0EBE2' }}
              data-testid="talent-optin-toggle"
            >
              <Checkbox
                id="talent-optin-toggle-cb"
                checked={talentOptInOnly}
                onCheckedChange={(v) => setTalentOptInOnly(!!v)}
              />
              <span className="text-sm" style={{ color: '#0F2942' }}>
                Email opt-in only
              </span>
            </label>

            <a
              href={`${API_URL}/api/talent/export/opted-in`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all hover:opacity-90"
              style={{ backgroundColor: '#D4820A', color: '#0F2942' }}
              data-testid="talent-export-optedin-csv"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} /> Export Opted-In CSV
            </a>
          </div>

          {/* Opt-in summary */}
          <div
            className="mb-6 rounded-lg px-4 py-3 text-sm flex flex-wrap gap-x-6 gap-y-1"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE2', color: '#0F2942' }}
            data-testid="talent-optin-summary"
          >
            <span>
              Showing <strong style={{ color: '#0F2942' }}>{talentProfiles.length}</strong>{' '}
              {talentProfiles.length === 1 ? 'profile' : 'profiles'}
            </span>
            <span>
              Opted-in:{' '}
              <strong style={{ color: '#D4820A' }} data-testid="talent-optin-count">
                {talentProfiles.filter((p) => p.email_opt_in).length}
              </strong>
            </span>
          </div>

          {loading ? (
            <p style={{ color: '#0F2942' }}>Loading...</p>
          ) : talentProfiles.length === 0 ? (
            <div className="text-center py-16">
              <User className="w-12 h-12 mx-auto mb-4" style={{ color: '#F0EBE2' }} strokeWidth={1} />
              <p style={{ color: '#0F2942' }}>No talent profiles found</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-talent-container">
              {talentProfiles.map((profile) => (
                <div
                  key={profile.id}
                  className="bg-white rounded-xl p-6 hover:shadow-md transition-all"
                  style={{ border: '1px solid #F0EBE2' }}
                  data-testid={`talent-${profile.id}`}
                >
                  <div className="flex justify-between items-start gap-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}>
                          {profile.primary_role}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.15em] font-medium rounded-full px-3 py-1" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>
                          {profile.career_stage}
                        </span>
                        {profile.email_opt_in && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-medium rounded-full px-2.5 py-1"
                            style={{ backgroundColor: 'rgba(212,130,10,0.12)', color: '#D4820A' }}
                            data-testid={`optin-badge-${profile.id}`}
                          >
                            <Mail className="w-3 h-3" strokeWidth={1.5} /> Subscribed
                          </span>
                        )}
                        <span className="text-xs" style={{ color: '#0F2942' }}>{formatDateTime(profile.submitted_at)}</span>
                      </div>

                      <h3 className="text-lg font-medium mb-1" style={{ color: '#0F2942' }}>{profile.full_name}</h3>

                      <div className="flex flex-wrap items-center gap-4 mb-2">
                        <a href={`mailto:${profile.email}`} className="text-sm flex items-center gap-1 hover:underline" style={{ color: '#0F2942' }}>
                          <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {profile.email}
                        </a>
                        <span className="text-sm flex items-center gap-1" style={{ color: '#0F2942' }}>
                          <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {profile.city}
                        </span>
                      </div>

                      {profile.opportunities_sought && profile.opportunities_sought.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {profile.opportunities_sought.map((o) => (
                            <span key={o} className="text-[10px] rounded-full px-2 py-0.5" style={{ backgroundColor: '#F0EBE2', color: '#0F2942' }}>{o}</span>
                          ))}
                        </div>
                      )}

                      <button onClick={() => handleViewTalent(profile)} className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }} data-testid={`view-talent-${profile.id}`}>
                        View Full Profile &rarr;
                      </button>
                    </div>

                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {(profile.resume_base64 || profile.resume_link) && (
                        profile.resume_base64 ? (
                          <a href={`${API_URL}/api/talent/${profile.id}/resume`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="rounded-lg text-xs w-full" data-testid={`download-resume-${profile.id}`}>
                              <FileText className="w-3.5 h-3.5 mr-1.5" /> Resume
                            </Button>
                          </a>
                        ) : (
                          <a href={profile.resume_link} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="rounded-lg text-xs w-full">
                              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Resume Link
                            </Button>
                          </a>
                        )
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteTalent(profile.id)} className="rounded-lg text-xs hover:bg-red-50 hover:text-red-600" style={{ color: '#0F2942' }}>
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
        <>
        <div className="flex items-center gap-4 mb-8 flex-wrap">
          <Select value={inquiryStatusFilter} onValueChange={setInquiryStatusFilter}>
            <SelectTrigger className="rounded-lg w-48 bg-white" data-testid="inquiry-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={inquiryTypeFilter} onValueChange={setInquiryTypeFilter}>
            <SelectTrigger className="rounded-lg w-56 bg-white" data-testid="inquiry-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="Hire Through Us">Hire Through Us</SelectItem>
              <SelectItem value="hire-through-us">hire-through-us</SelectItem>
              <SelectItem value="Portfolio Review">Portfolio Review</SelectItem>
              <SelectItem value="General">General</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm" style={{ color: '#0F2942' }}>{inquiries.length} inquiries</p>
          <div className="flex-1" />
          <Button onClick={exportInquiriesCsv} className="rounded-lg" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }} data-testid="export-inquiries-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {loading ? (
          <p className="text-sm" style={{ color: '#0F2942' }}>Loading...</p>
        ) : inquiries.length === 0 ? (
          <div className="rounded-xl p-12 text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE2' }}>
            <p className="text-base" style={{ color: '#0F2942' }}>No inquiries yet.</p>
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE2' }}>
            <table className="w-full">
              <thead style={{ backgroundColor: '#F0EBE2' }}>
                <tr>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Received</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>From</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Organisation</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Type</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Status</th>
                  <th className="px-6 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {inquiries.map((inq) => (
                  <tr key={inq.id} className="border-t" style={{ borderColor: '#F0EBE2' }} data-testid={`inquiry-row-${inq.id}`}>
                    <td className="px-6 py-4 text-sm" style={{ color: '#0F2942' }}>
                      {inq.submitted_at ? new Date(inq.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium" style={{ color: '#0F2942' }}>{inq.name || '—'}</p>
                      <a href={`mailto:${inq.email}`} className="text-xs hover:underline" style={{ color: '#0F2942' }}>{inq.email}</a>
                    </td>
                    <td className="px-6 py-4 text-sm" style={{ color: '#0F2942' }}>
                      {inq.organisation || '—'}
                      {inq.role && <p className="text-xs mt-0.5" style={{ color: '#0F2942' }}>{inq.role}</p>}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs rounded-full px-3 py-1" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>
                        {inq.inquiry_type || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs rounded-full px-3 py-1 capitalize" style={{
                        backgroundColor: inq.status === 'new' ? 'rgba(212,130,10,0.15)' : inq.status === 'contacted' ? 'rgba(212,130,10,0.2)' : 'rgba(15,41,66,0.08)',
                        color: inq.status === 'new' ? '#D4820A' : inq.status === 'contacted' ? '#D4820A' : '#0F2942'
                      }}>
                        {inq.status || 'new'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg"
                        onClick={() => { setSelectedInquiry(inq); setShowInquiryDialog(true); }}
                        data-testid={`inquiry-view-${inq.id}`}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {/* Crew Tab */}
        {activeTab === 'crew' && (
        <>
        <div className="flex items-center gap-4 mb-8 flex-wrap">
          <p className="text-sm" style={{ color: '#0F2942' }}>{crewProfiles.length} crew profiles</p>
          <div className="flex-1" />
          <Button onClick={exportCrewCsv} className="rounded-lg" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }} data-testid="export-crew-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
        {loading ? (
          <p className="text-sm" style={{ color: '#0F2942' }}>Loading...</p>
        ) : crewProfiles.length === 0 ? (
          <div className="rounded-xl p-12 text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE2' }}>
            <p className="text-base" style={{ color: '#0F2942' }}>No crew profiles yet.</p>
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE2' }}>
            <table className="w-full">
              <thead style={{ backgroundColor: '#F0EBE2' }}>
                <tr>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Received</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Name</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Phone (WhatsApp)</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>City</th>
                  <th className="text-left text-xs uppercase tracking-[0.15em] font-medium px-6 py-3.5" style={{ color: '#0F2942' }}>Roles</th>
                  <th className="px-6 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {crewProfiles.map((c) => (
                  <tr key={c.id} className="border-t" style={{ borderColor: '#F0EBE2' }} data-testid={`crew-row-${c.id}`}>
                    <td className="px-6 py-4 text-sm" style={{ color: '#0F2942' }}>
                      {c.submitted_at ? new Date(c.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium" style={{ color: '#0F2942' }}>{c.full_name}</p>
                      <a href={`mailto:${c.email}`} className="text-xs hover:underline" style={{ color: '#0F2942' }}>{c.email}</a>
                    </td>
                    <td className="px-6 py-4 text-sm" style={{ color: '#0F2942' }}>
                      <a href={`https://wa.me/${(c.phone||'').replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{c.phone}</a>
                    </td>
                    <td className="px-6 py-4 text-sm" style={{ color: '#0F2942' }}>{c.city}</td>
                    <td className="px-6 py-4 text-sm" style={{ color: '#0F2942' }}>
                      <div className="flex flex-wrap gap-1">
                        {(c.gig_roles||[]).slice(0,3).map((r) => (
                          <span key={r} className="text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>{r}</span>
                        ))}
                        {(c.gig_roles||[]).length > 3 && <span className="text-xs" style={{ color: '#0F2942' }}>+{c.gig_roles.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openCrewDetail(c.id)} data-testid={`crew-view-${c.id}`}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      {/* Talent Detail Dialog */}
      <Dialog open={showTalentDetailDialog} onOpenChange={setShowTalentDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Talent Profile</DialogTitle>
          </DialogHeader>
          {selectedTalent && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}>{selectedTalent.primary_role}</span>
                <span className="text-xs uppercase tracking-[0.15em] font-medium rounded-full px-3 py-1" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>{selectedTalent.career_stage}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Name</p><p className="text-sm font-medium">{selectedTalent.full_name}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Email</p><a href={`mailto:${selectedTalent.email}`} className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }}>{selectedTalent.email}</a></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Location</p><p className="text-sm">{selectedTalent.city}, {selectedTalent.country}</p></div>
                {selectedTalent.current_role_description && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Current Role</p><p className="text-sm">{selectedTalent.current_role_description}</p></div>}
              </div>
              {(selectedTalent.portfolio_url || selectedTalent.linkedin_url || selectedTalent.instagram_url) && (
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>Links</p>
                  <div className="flex flex-wrap gap-3">
                    {selectedTalent.portfolio_url && <a href={selectedTalent.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#0F2942' }}>Portfolio</a>}
                    {selectedTalent.linkedin_url && <a href={selectedTalent.linkedin_url.startsWith('http') ? selectedTalent.linkedin_url : `https://${selectedTalent.linkedin_url}`} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#0F2942' }}>LinkedIn</a>}
                    {selectedTalent.instagram_url && <a href={`https://instagram.com/${selectedTalent.instagram_url.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#0F2942' }}>Instagram</a>}
                  </div>
                </div>
              )}
              {selectedTalent.opportunities_sought?.length > 0 && (
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>Looking For</p>
                  <div className="flex flex-wrap gap-1.5">{selectedTalent.opportunities_sought.map((o) => <span key={o} className="text-xs rounded-full px-2.5 py-1" style={{ backgroundColor: '#F0EBE2', color: '#0F2942' }}>{o}</span>)}</div>
                </div>
              )}
              {selectedTalent.career_goal && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Career Goal</p><p className="text-sm">{selectedTalent.career_goal}</p></div>}
              {selectedTalent.skills && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Skills</p><p className="text-sm">{selectedTalent.skills}</p></div>}
              {selectedTalent.open_to?.length > 0 && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Open To</p><p className="text-sm">{selectedTalent.open_to.join(', ')}</p></div>}
              {selectedTalent.about && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>About</p><p className="text-sm leading-relaxed p-3 rounded-lg" style={{ backgroundColor: '#F0EBE2' }}>{selectedTalent.about}</p></div>}
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>Your Notes</p>
                <Textarea value={talentNotes} onChange={(e) => setTalentNotes(e.target.value)} rows={3} className="rounded-lg mb-2" placeholder="Add notes about this candidate..." />
                <Button size="sm" onClick={handleSaveTalentNotes} className="rounded-lg" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }} data-testid="save-talent-notes">Save Notes</Button>
              </div>
              <div className="flex gap-3 pt-2">
                <a href={`mailto:${selectedTalent.email}`}><Button className="rounded-lg" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}><Mail className="w-4 h-4 mr-2" /> Email</Button></a>
                {selectedTalent.resume_base64 && <a href={`${API_URL}/api/talent/${selectedTalent.id}/resume`} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-lg"><FileText className="w-4 h-4 mr-2" /> Download Resume</Button></a>}
                {selectedTalent.resume_link && <a href={selectedTalent.resume_link} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-lg"><ExternalLink className="w-4 h-4 mr-2" /> Resume Link</Button></a>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Application Detail Dialog */}
      <Dialog open={showAppDetailDialog} onOpenChange={setShowAppDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-5" data-testid="application-detail-panel">
              <div className="flex flex-wrap items-center gap-2">
                {selectedApp.kind === 'direct' && (
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#F0EBE2' }}>
                    Direct recruitment
                  </span>
                )}
                <span className="text-xs uppercase tracking-[0.15em] font-medium rounded-full px-3 py-1 capitalize" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>
                  {selectedApp.status || 'submitted'}
                </span>
                {selectedApp.submitted_at && (
                  <span className="text-xs" style={{ color: '#0F2942' }}>{formatDateTime(selectedApp.submitted_at)}</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Name</p>
                  <p className="text-sm font-medium" style={{ color: '#0F2942' }}>{selectedApp.applicant_name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Email</p>
                  <a href={`mailto:${selectedApp.applicant_email}`} className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }}>
                    {selectedApp.applicant_email}
                  </a>
                </div>
                {selectedApp.phone && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Phone (WhatsApp)</p>
                    <a href={`https://wa.me/${(selectedApp.phone||'').replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }}>
                      {selectedApp.phone}
                    </a>
                  </div>
                )}
                {selectedApp.current_city && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Current city</p>
                    <p className="text-sm" style={{ color: '#0F2942' }}>{selectedApp.current_city}</p>
                  </div>
                )}
                {selectedApp.years_experience && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Experience</p>
                    <p className="text-sm" style={{ color: '#0F2942' }}>{selectedApp.years_experience}</p>
                  </div>
                )}
                {selectedApp.notice_period && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Notice period</p>
                    <p className="text-sm" style={{ color: '#0F2942' }}>{selectedApp.notice_period}</p>
                  </div>
                )}
                {selectedApp.expected_salary && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Expected salary</p>
                    <p className="text-sm" style={{ color: '#0F2942' }}>{selectedApp.expected_salary}</p>
                  </div>
                )}
                {selectedApp.linkedin_url && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>LinkedIn</p>
                    <a href={selectedApp.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline break-all" style={{ color: '#0F2942' }}>
                      {selectedApp.linkedin_url}
                    </a>
                  </div>
                )}
                {selectedApp.portfolio_link && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Portfolio / Resume / Showreel</p>
                    <a href={selectedApp.portfolio_link} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline break-all" style={{ color: '#0F2942' }}>
                      {selectedApp.portfolio_link}
                    </a>
                  </div>
                )}
              </div>

              {(selectedApp.portfolio_file_base64 || selectedApp.resume_url) && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>Uploaded file</p>
                  {selectedApp.portfolio_file_base64 ? (
                    <a
                      href={selectedApp.portfolio_file_base64}
                      download={selectedApp.portfolio_file_name || 'attachment'}
                      className="inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-full hover:opacity-90"
                      style={{ backgroundColor: '#0F2942', color: '#F0EBE2' }}
                      data-testid="application-download-attachment"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download {selectedApp.portfolio_file_name || 'attachment'}
                    </a>
                  ) : (
                    <a href={selectedApp.resume_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#0F2942' }}>
                      {selectedApp.resume_url}
                    </a>
                  )}
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Applied for</p>
                <p className="text-sm font-medium" style={{ color: '#0F2942' }}>{selectedApp.listing_title}</p>
                <p className="text-xs" style={{ color: '#0F2942' }}>{selectedApp.listing_organisation}</p>
              </div>

              {selectedApp.cover_letter && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>Cover letter</p>
                  <div
                    className="p-4 rounded-lg text-sm leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto"
                    style={{ backgroundColor: '#F0EBE2', color: '#0F2942', borderLeft: '3px solid #D4820A' }}
                    data-testid="application-cover-letter"
                  >
                    {selectedApp.cover_letter}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-3 border-t sticky bottom-0 bg-white pb-1" style={{ borderColor: 'rgba(15,41,66,0.1)' }}>
                <a href={`mailto:${selectedApp.applicant_email}`}>
                  <Button className="rounded-full px-5" style={{ backgroundColor: '#D4820A', color: '#0F2942' }} data-testid="reply-email-button">
                    <Mail className="w-4 h-4 mr-2" /> Reply via email
                  </Button>
                </a>
                {selectedApp.phone && (
                  <a href={`https://wa.me/${(selectedApp.phone||'').replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="rounded-full px-5" style={{ borderColor: '#0F2942', color: '#0F2942' }} data-testid="reply-whatsapp-button">
                      WhatsApp
                    </Button>
                  </a>
                )}
                <Select
                  value={selectedApp.status}
                  onValueChange={(val) => { handleAppStatusChange(selectedApp.id, val); setSelectedApp({ ...selectedApp, status: val }); }}
                >
                  <SelectTrigger className="rounded-full w-40" data-testid="application-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Listing Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Listing (Auto-Published)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddListing} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input
                value={newListing.title}
                onChange={(e) => setNewListing({ ...newListing, title: e.target.value })}
                required
                className="rounded-none"
                placeholder="e.g. Curator of Contemporary Art"
                data-testid="add-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Opportunity Type *</label>
                <Select value={newListing.opportunity_type} onValueChange={(val) => setNewListing({ ...newListing, opportunity_type: val })}>
                  <SelectTrigger className="rounded-none" data-testid="add-opportunity-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPPORTUNITY_TYPES.map((ot) => (
                      <SelectItem key={ot} value={ot}>{ot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Industry *</label>
                <Select value={newListing.industry} onValueChange={(val) => setNewListing({ ...newListing, industry: val })}>
                  <SelectTrigger className="rounded-none" data-testid="add-industry">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Organisation *</label>
              <Input
                value={newListing.organisation}
                onChange={(e) => setNewListing({ ...newListing, organisation: e.target.value })}
                required
                className="rounded-none"
                data-testid="add-organisation-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">City *</label>
                <Input
                  value={newListing.city}
                  onChange={(e) => setNewListing({ ...newListing, city: e.target.value })}
                  required
                  className="rounded-none"
                  data-testid="add-city-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Country *</label>
                <Input
                  value={newListing.country}
                  onChange={(e) => setNewListing({ ...newListing, country: e.target.value })}
                  required
                  className="rounded-none"
                  data-testid="add-country-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Salary</label>
                <Input
                  value={newListing.salary}
                  onChange={(e) => setNewListing({ ...newListing, salary: e.target.value })}
                  className="rounded-none"
                  data-testid="add-salary-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Closing Date *</label>
                <Input
                  type="date"
                  value={newListing.closing_date ? newListing.closing_date.split('T')[0] : ''}
                  onChange={(e) => setNewListing({ ...newListing, closing_date: new Date(e.target.value + 'T23:59:59Z').toISOString() })}
                  required
                  className="rounded-none"
                  data-testid="add-closing-date"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description *</label>
              <RichTextEditor
                content={newListing.description}
                onChange={(html) => setNewListing({ ...newListing, description: html })}
                placeholder="Describe the opportunity..."
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Skill Tags</label>
              <div className="flex flex-wrap gap-1.5">
                {SKILL_TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleNewTag(tag)}
                    className={`px-2.5 py-1 text-xs border transition-colors ${
                      newListing.tags.includes(tag) ? 'bg-black text-white border-black' : 'bg-white text-zinc-600 border-zinc-300 hover:border-black'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Application Link</label>
              <Input
                value={newListing.apply_link}
                onChange={(e) => setNewListing({ ...newListing, apply_link: e.target.value })}
                className="rounded-none"
                placeholder="https://..."
                data-testid="add-apply-link"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="add-platform-app"
                checked={newListing.allow_platform_application}
                onCheckedChange={(checked) => setNewListing({ ...newListing, allow_platform_application: checked })}
                data-testid="add-platform-checkbox"
              />
              <label htmlFor="add-platform-app" className="text-sm text-zinc-700">Allow platform applications</label>
            </div>
            <Button
              type="submit"
              disabled={addSubmitting}
              className="rounded-none bg-black text-white hover:bg-zinc-800"
              data-testid="add-submit-button"
            >
              {addSubmitting ? 'Creating...' : 'Create & Publish'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Listing</DialogTitle>
          </DialogHeader>
          {editingListing && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Title</label>
                <Input
                  value={editingListing.title}
                  onChange={(e) => setEditingListing({ ...editingListing, title: e.target.value })}
                  className="rounded-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Opportunity Type</label>
                  <Select
                    value={editingListing.opportunity_type}
                    onValueChange={(val) => setEditingListing({ ...editingListing, opportunity_type: val })}
                  >
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPPORTUNITY_TYPES.map((ot) => (
                        <SelectItem key={ot} value={ot}>{ot}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Industry</label>
                  <Select
                    value={editingListing.industry}
                    onValueChange={(val) => setEditingListing({ ...editingListing, industry: val })}
                  >
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Organisation</label>
                <Input
                  value={editingListing.organisation}
                  onChange={(e) => setEditingListing({ ...editingListing, organisation: e.target.value })}
                  className="rounded-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">City</label>
                  <Input
                    value={editingListing.city}
                    onChange={(e) => setEditingListing({ ...editingListing, city: e.target.value })}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Country</label>
                  <Input
                    value={editingListing.country}
                    onChange={(e) => setEditingListing({ ...editingListing, country: e.target.value })}
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <RichTextEditor
                  content={editingListing.description}
                  onChange={(html) => setEditingListing({ ...editingListing, description: html })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Salary / Compensation</label>
                  <Input
                    value={editingListing.salary || ''}
                    onChange={(e) => setEditingListing({ ...editingListing, salary: e.target.value })}
                    className="rounded-none"
                    placeholder="e.g. ₹6-8L per annum"
                    data-testid="edit-salary"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Closing date</label>
                  <Input
                    type="date"
                    value={(editingListing.closing_date || '').slice(0, 10)}
                    onChange={(e) => setEditingListing({ ...editingListing, closing_date: e.target.value })}
                    className="rounded-none"
                    data-testid="edit-closing-date"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">External apply link (used only if platform-application is off)</label>
                <Input
                  value={editingListing.apply_link || ''}
                  onChange={(e) => setEditingListing({ ...editingListing, apply_link: e.target.value })}
                  className="rounded-none"
                  placeholder="https://..."
                  data-testid="edit-apply-link"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!editingListing.allow_platform_application}
                    onChange={(e) => setEditingListing({ ...editingListing, allow_platform_application: e.target.checked })}
                    data-testid="edit-allow-platform"
                  />
                  Allow platform applications
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!editingListing.direct_recruitment}
                    onChange={(e) => setEditingListing({ ...editingListing, direct_recruitment: e.target.checked, allow_platform_application: e.target.checked ? true : editingListing.allow_platform_application })}
                    data-testid="edit-direct-recruitment"
                  />
                  Direct recruitment by CultureCareers
                </label>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Status</label>
                <Select
                  value={editingListing.status}
                  onValueChange={(val) => setEditingListing({ ...editingListing, status: val })}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid="save-edit-button">
                Save Changes
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Inquiry Detail Dialog */}
      <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-5" data-testid="inquiry-detail-panel">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}>
                  {selectedInquiry.inquiry_type || 'General'}
                </span>
                <span className="text-xs uppercase tracking-[0.15em] font-medium rounded-full px-3 py-1 capitalize" style={{
                  backgroundColor: selectedInquiry.status === 'new' ? 'rgba(212,130,10,0.15)' : selectedInquiry.status === 'contacted' ? 'rgba(212,130,10,0.2)' : 'rgba(15,41,66,0.08)',
                  color: selectedInquiry.status === 'new' ? '#D4820A' : selectedInquiry.status === 'contacted' ? '#D4820A' : '#0F2942'
                }}>
                  {selectedInquiry.status || 'new'}
                </span>
                {selectedInquiry.submitted_at && (
                  <span className="text-xs" style={{ color: '#0F2942' }}>
                    {new Date(selectedInquiry.submitted_at).toLocaleString('en-IN')}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Name</p><p className="text-sm font-medium">{selectedInquiry.name || '—'}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Email</p><a href={`mailto:${selectedInquiry.email}`} className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }}>{selectedInquiry.email}</a></div>
                {selectedInquiry.phone && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Phone</p><a href={`tel:${selectedInquiry.phone}`} className="text-sm hover:underline" style={{ color: '#0F2942' }}>{selectedInquiry.phone}</a></div>}
                {selectedInquiry.organisation && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Organisation</p><p className="text-sm">{selectedInquiry.organisation}</p></div>}
                {selectedInquiry.organisation_type && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Org type</p><p className="text-sm">{selectedInquiry.organisation_type}</p></div>}
                {selectedInquiry.role && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Role to hire</p><p className="text-sm">{selectedInquiry.role}</p></div>}
                {selectedInquiry.timeline && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Timeline</p><p className="text-sm">{selectedInquiry.timeline}</p></div>}
              </div>
              {selectedInquiry.message && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>Message</p>
                  <div className="rounded-lg p-4 text-sm leading-relaxed" style={{ backgroundColor: '#F0EBE2', borderLeft: '3px solid #D4820A', color: '#0F2942', whiteSpace: 'pre-wrap' }}>
                    {selectedInquiry.message}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: '#F0EBE2' }}>
                <a href={`mailto:${selectedInquiry.email}`}>
                  <Button className="rounded-lg" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }} data-testid="inquiry-email-btn">
                    <Mail className="w-4 h-4 mr-2" /> Reply by email
                  </Button>
                </a>
                {selectedInquiry.status !== 'contacted' && (
                  <Button variant="outline" className="rounded-lg" onClick={() => updateInquiryStatus(selectedInquiry.id, 'contacted')} data-testid="inquiry-mark-contacted">
                    Mark as Contacted
                  </Button>
                )}
                {selectedInquiry.status !== 'closed' && (
                  <Button variant="outline" className="rounded-lg" onClick={() => updateInquiryStatus(selectedInquiry.id, 'closed')} data-testid="inquiry-mark-closed">
                    Mark as Closed
                  </Button>
                )}
                {selectedInquiry.status !== 'new' && (
                  <Button variant="outline" className="rounded-lg" onClick={() => updateInquiryStatus(selectedInquiry.id, 'new')}>
                    Reopen
                  </Button>
                )}
                <div className="flex-1" />
                <Button
                  variant="outline"
                  className="rounded-lg"
                  style={{ color: '#D4820A', borderColor: 'rgba(212,130,10,0.4)' }}
                  onClick={() => deleteInquiry(selectedInquiry.id)}
                  data-testid="inquiry-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Crew Detail Dialog */}
      <Dialog open={showCrewDialog} onOpenChange={setShowCrewDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crew Profile</DialogTitle>
          </DialogHeader>
          {selectedCrew && (
            <div className="space-y-5" data-testid="crew-detail-panel">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}>
                  Crew
                </span>
                <span className="text-xs uppercase tracking-[0.15em] font-medium rounded-full px-3 py-1 capitalize" style={{ backgroundColor: selectedCrew.status === 'new' ? 'rgba(212,130,10,0.15)' : 'rgba(15,41,66,0.08)', color: selectedCrew.status === 'new' ? '#D4820A' : '#0F2942' }}>
                  {selectedCrew.status || 'new'}
                </span>
                {selectedCrew.submitted_at && (
                  <span className="text-xs" style={{ color: '#0F2942' }}>{new Date(selectedCrew.submitted_at).toLocaleString('en-IN')}</span>
                )}
              </div>

              {selectedCrew.profile_photo_base64 && (
                <img src={selectedCrew.profile_photo_base64} alt={selectedCrew.full_name} className="w-32 h-32 rounded-lg object-cover" />
              )}

              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Name</p><p className="text-sm font-medium">{selectedCrew.full_name}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>City</p><p className="text-sm">{selectedCrew.city}</p></div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Phone (WhatsApp)</p>
                  <a href={`https://wa.me/${(selectedCrew.phone||'').replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }}>{selectedCrew.phone}</a>
                </div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Email</p><a href={`mailto:${selectedCrew.email}`} className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }}>{selectedCrew.email}</a></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Travels</p><p className="text-sm">{selectedCrew.travels}</p></div>
                {selectedCrew.travel_cities && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Travel cities</p><p className="text-sm">{selectedCrew.travel_cities}</p></div>}
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Events worked</p><p className="text-sm">{selectedCrew.events_worked}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Cultural exp</p><p className="text-sm">{selectedCrew.cultural_experience}{selectedCrew.cultural_example ? ` — ${selectedCrew.cultural_example}` : ''}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Short notice</p><p className="text-sm">{selectedCrew.short_notice}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Notice period</p><p className="text-sm">{selectedCrew.notice_period}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Days</p><p className="text-sm">{(selectedCrew.days_available||[]).join(', ')}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Employment</p><p className="text-sm">{selectedCrew.employment_status}</p></div>
                {selectedCrew.instagram_handle && <div><p className="text-xs uppercase tracking-[0.15em] font-medium mb-1" style={{ color: '#0F2942' }}>Instagram</p><a href={`https://instagram.com/${selectedCrew.instagram_handle.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#0F2942' }}>{selectedCrew.instagram_handle}</a></div>}
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>Gig roles</p>
                <div className="flex flex-wrap gap-1.5">
                  {(selectedCrew.gig_roles||[]).map((r) => (
                    <span key={r} className="text-xs rounded-full px-2.5 py-1" style={{ backgroundColor: '#F0EBE2', color: '#0F2942' }}>{r}</span>
                  ))}
                </div>
                {selectedCrew.gig_roles_other && <p className="text-xs mt-2" style={{ color: '#0F2942' }}>Other: {selectedCrew.gig_roles_other}</p>}
              </div>

              {selectedCrew.about_strength && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium mb-2" style={{ color: '#0F2942' }}>What makes them good at this</p>
                  <div className="rounded-lg p-4 text-sm" style={{ backgroundColor: '#F0EBE2', borderLeft: '3px solid #D4820A', color: '#0F2942' }}>{selectedCrew.about_strength}</div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: '#F0EBE2' }}>
                <a href={`https://wa.me/${(selectedCrew.phone||'').replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-lg" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }} data-testid="crew-wa-btn">
                    WhatsApp
                  </Button>
                </a>
                <a href={`mailto:${selectedCrew.email}`}>
                  <Button variant="outline" className="rounded-lg"><Mail className="w-4 h-4 mr-2" /> Email</Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
